import { HTMLAttributes, useEffect } from 'react';
import { ControllerRenderProps } from 'react-hook-form';

import { CheckboxModel } from '../../../checkbox/checkbox-model';
import { Attribute } from '../attribute';
import { FieldWrapper } from '../field-wrapper';

/**
 * Props for rendering checkbox controls inside dynamic form renderer.
 */
interface CheckboxRenderProps extends Pick<HTMLAttributes<HTMLDivElement>, 'className'> {
  /** Schema attribute that defines checkbox configuration and options model. */
  attribute: Attribute;
  /** React Hook Form field controller for reading and updating the field value. */
  field: ControllerRenderProps<any>;
  /** Loading state forwarded to checkbox UI. */
  isLoading?: boolean;
}

/**
 * Renders a checkbox model and sanitizes selected values against current options.
 *
 * During edit flows, persisted values may include options removed from the model.
 * This component filters those stale values and syncs the cleaned array back to RHF
 * so submit payload only contains existing options.
 */
export function CheckboxRender({ attribute, className, field, isLoading }: CheckboxRenderProps) {
  const { id, disabled, model, checkbox } = attribute;

  const checkboxModelValues = new Set(
    (model ?? [])
      .map((item) => item?.value)
      .filter((item): item is string => typeof item === 'string' && item.trim() !== ''),
  );

  const checkboxCurrentValue = Array.isArray(field.value)
    ? field.value.filter((item): item is string => typeof item === 'string')
    : [];

  const checkboxSanitizedValue = checkboxCurrentValue.filter((item) =>
    checkboxModelValues.has(item),
  );

  const hasInvalidCheckboxValues = checkboxSanitizedValue.length !== checkboxCurrentValue.length;

  useEffect(() => {
    if (!hasInvalidCheckboxValues) return;
    field.onChange(checkboxSanitizedValue);
  }, [field, hasInvalidCheckboxValues, checkboxSanitizedValue]);

  return (
    <FieldWrapper className={className} {...attribute}>
      <CheckboxModel
        className={checkbox?.className ?? ''}
        disabled={disabled}
        id={id}
        isLoading={isLoading}
        model={model ?? []}
        type={checkbox?.type}
        value={checkboxSanitizedValue}
        onValueChange={(value) =>
          field.onChange(value.filter((item) => checkboxModelValues.has(item)))
        }
      />
    </FieldWrapper>
  );
}
